import {
  HEALTH_LABEL,
  HEALTH_TONE,
  MONETIZATION_LABEL,
  MONETIZATION_TONE,
  ngayDayVn,
  type ChannelRow,
  type HealthStatus,
  type MonetizationStatus,
} from '@qlk/shared';
import { KeyRound, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Avatar, NhanDangLayLichSu, StatusBadge } from '../../../ui/index.js';

/**
 * Ô "Kênh": avatar **vuông bo 8px**, tên bấm được, dòng phụ "chủ đề · ngôn
 * ngữ", và **icon cảnh báo** cho hai thứ từng chiếm hẳn một cột.
 *
 * Bước 2/3 bỏ hai cột "Chủ sở hữu chính" và "Sức khỏe" khỏi bảng: cột chủ sở
 * hữu chỉ có nghĩa khi **chưa khai** (rủi ro mất quyền tài khoản thương hiệu),
 * còn cột sức khỏe thì 11/12 hàng ghi "Bình thường". Hai thứ chỉ đáng chỗ khi
 * khác thường, nên nay là icon cạnh tên, có tooltip giải thích; email và ghi
 * chú đầy đủ xem trong trang kênh.
 */
export function OKenh({ kenh }: { kenh: ChannelRow }) {
  const thieuChuSoHuu = kenh.brandPrimaryOwnerEmail === null;
  const sucKhoeLa = kenh.healthStatus !== 'ok';

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={kenh.name} color={kenh.avatarColor} size={32} vuong />
      <div className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5">
          <Link to={`/kenh/${kenh.id}`} className="truncate font-semibold text-chu hover:text-nhan">
            {kenh.name}
          </Link>

          {sucKhoeLa && (
            /* Tooltip đặt ở `span`: icon của lucide không nhận prop `title`. */
            <span
              role="img"
              aria-label={`Sức khỏe: ${HEALTH_LABEL[kenh.healthStatus]}`}
              title={`Sức khỏe: ${HEALTH_LABEL[kenh.healthStatus]}${
                kenh.healthNote === null ? '' : ` — ${kenh.healthNote}`
              }`}
              className={`shrink-0 cursor-help ${
                kenh.healthStatus === 'bad' ? 'text-bad-chu' : 'text-warn-chu'
              }`}
            >
              <TriangleAlert size={14} aria-hidden="true" />
            </span>
          )}

          {thieuChuSoHuu && (
            /* Chưa khai chủ sở hữu chính là rủi ro thật: mất quyền tài khoản
               thương hiệu là mất kênh. */
            <span
              role="img"
              aria-label="Chưa khai chủ sở hữu chính của Tài khoản thương hiệu"
              title="Chưa khai chủ sở hữu chính của Tài khoản thương hiệu — mất quyền là mất kênh. Khai trong tab Cài đặt kênh."
              className="shrink-0 cursor-help text-warn-chu"
            >
              <KeyRound size={14} aria-hidden="true" />
            </span>
          )}
        </span>

        <p className="truncate text-phu text-chu-phu">
          {kenh.niche} · {kenh.language}
        </p>

        {/* Kênh bị từ chối / bị tắt: mốc YouTube cho nộp lại đơn kiếm tiền. */}
        {kenh.monetizationReapplyAt !== null && (
          <p className="so-lieu truncate text-phu text-warn-chu">
            Nộp lại từ {ngayDayVn(kenh.monetizationReapplyAt)}
          </p>
        )}

        {/* Kênh mới kết nối: nói rõ đang lấy lịch sử, để không ai đọc số 28 ngày như số thật. */}
        {kenh.syncStatus?.lichSu?.dangChay && (
          <p className="mt-1">
            <NhanDangLayLichSu lichSu={kenh.syncStatus.lichSu} />
          </p>
        )}
      </div>
    </div>
  );
}

export function BadgeKiemTien({ trangThai }: { trangThai: MonetizationStatus }) {
  return (
    <StatusBadge tone={MONETIZATION_TONE[trangThai]}>{MONETIZATION_LABEL[trangThai]}</StatusBadge>
  );
}

export function BadgeSucKhoe({
  trangThai,
  ghiChu,
}: {
  trangThai: HealthStatus;
  ghiChu?: string | null;
}) {
  return (
    <StatusBadge tone={HEALTH_TONE[trangThai]} className={ghiChu ? 'cursor-help' : ''}>
      <span title={ghiChu ?? undefined}>{HEALTH_LABEL[trangThai]}</span>
    </StatusBadge>
  );
}
